import _ from 'lodash'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Modal, message } from 'antd'
import { getAttachmentTableItems } from '@Admin/AdminQuoteDetailsPage/logic'

import { getCoreQuoteDetailsData } from '~/redux-modules/query-refactor/actions'
import { DFM_FAILURE, WARN_TYPE } from '~/lib/partsConstants'
import Box from '~/core/Box'
import {
    REQUESTED,
    DRAFT,
    PURCHASED,
    QUANTITY_RANGE_UNAVAILABLE,
} from '~/lib/quoteStatuses'
import { DEFAULT_VALUE_DASH } from '~/lib/getPriceDisplay'
import logger from '~/lib/logger'
import { hasProcessingAttachment } from '../../QuoteDetailPage/selectors'
import { NOT_AVAILABLE } from '~/apps/consumer/pages/Orders/OrderDetailsPage/constants'
import { UpdateQuoteStatusToDraftCheckoutSummaryDocument } from '~/gql/graphql'

import {
    INFO_TYPE,
    NO_PARTS_CONFIGURED_MESSAGE,
    PROJECT_REQUIREMENTS_AND_PARTS_NOT_CONFIG_MESSAGE,
    MANUAL_QUOTE_REQUIRED_MESSAGE,
    IM_MANUAL_QUOTE_REQUIRED_MESSAGE,
    QUOTE_EXPIRED,
    NOT_PURCHASABLE_MESSAGE,
} from './constants'

// https://momentjs.com/docs/#/displaying/format/
export const DEFAULT_DATE_FORMAT = 'dddd, MMMM DD, YYYY'
export const DATE_FORMAT_WITHOUT_YEAR = 'dddd, MMMM DD'
export const DATE_FORMAT_MM_DD_YYYY = 'MM-DD-YYYY'
// www.figma.com/file/xUmKWddDDSrBosPWOePeQm/Growth---Purchasing-22'?node-id=1498%3A15193

export const PST_TIMEZONE = 'America/Los_Angeles'

export function areAllPartsInQuoteConfigured(quoteModels) {
    const configured = _.filter(quoteModels, quoteModel => {
        return _.get(
            quoteModel,
            'selectedQuoteModelRevision.modelConfiguration',
        )
    })

    return configured.length === quoteModels?.length
}

export function somePartsInQuoteHaveProcessingDrawing(quoteModels) {
    return quoteModels.some(hasProcessingAttachment)
}

export function updateQuoteStatusToDraft({ client, quoteId }) {
    const optimisticResponse = {
        updateQuoteStatusToDraft: {
            id: quoteId,
            status: DRAFT,
            isQuoteInRfqState: true,
            __typename: 'Quote',
        },
    }

    return client
        .mutate({
            mutation: UpdateQuoteStatusToDraftCheckoutSummaryDocument,
            variables: { id: quoteId },
            optimisticResponse,
        })
        .then(({ data }) => {
            return data.updateQuoteStatusToDraft
        })
}

export const updateQuoteToDraft = async (
    client,
    quoteId,
    isQueryRefactorEnabled,
    dispatch,
    isCpqFlagEnabled,
) => {
    try {
        if (isQueryRefactorEnabled) {
            await updateQuoteStatusToDraft({
                client,
                quoteId: quoteId,
            }).then(() => {
                if (isCpqFlagEnabled) {
                    window.localStorage.removeItem('modal-status')
                }
                dispatch(getCoreQuoteDetailsData(quoteId))
                return message.success('Canceled request for quote')
            })
        } else {
            await updateQuoteStatusToDraft({
                client,
                quoteId: quoteId,
            })
            if (isCpqFlagEnabled) {
                window.localStorage.removeItem('modal-status')
            }
            dispatch(getCoreQuoteDetailsData(quoteId))
            return message.success('Canceled request for quote')
        }
    } catch (error) {
        logger.exception(error, {
            extra: {
                info: 'QuoteCheckoutSummary::Failed to updateQuoteStatusToDraft from QuoteCheckoutSummary',
                quoteId,
            },
        })
        return message.error(
            'Quote request could not be canceled. Please try again or get in touch with our team.',
        )
    }
}

export const onCancelRFQ = (
    client,
    quoteId,
    isQueryRefactorEnabled = false,
    dispatch,
    isCpqFlagEnabled,
) => {
    return Modal.confirm({
        title: 'Are you sure you want to make edits?',
        content:
            'Editing will pause your current request. Please remember to click “Request quote” once you’re done editing so that we can resume processing your request for quote.',
        maskClosable: true,
        icon: <QuestionCircleOutlined />,
        onOk: () =>
            updateQuoteToDraft(
                client,
                quoteId,
                isQueryRefactorEnabled,
                dispatch,
                isCpqFlagEnabled,
            ),
        okText: 'Edit quote',
        cancelText: 'Cancel',
        onCancel() {
            /* noop */
        },
        width: 500,
    })
}

export function getManualQuoteMessage(params) {
    const {
        isQuotePurchasable,
        status,
        areAllPartsConfigured,
        partsHaveProcessingDrawings,
        isQuoteInRfqState,
        quoteModelLength,
        isImQuoteType,
        isExpired,
        quotePurchaser,
        orderPlacedAt,
        cpqQuote,
    } = params
    const DATA_TEST_TARGET = 'alert-message-quote-request'

    const genericQuoteStatusMessageComponent = (
        <Box
            data-test-target={DATA_TEST_TARGET}
            display="inlineBlock"
            fontSize="14px"
            style={{
                fontWeight: '300',
                marginTop: '2px',
            }}
        >
            Quote requested! You’ll receive an email notification when your
            quote is ready. You can still make edits in the meantime.
        </Box>
    )

    const quotePurchasedStatusMessage = (
        <Box
            display="inlineBlock"
            fontSize="14px"
            style={{
                fontWeight: '300',
                marginTop: '2px',
            }}
        >
            This quote was purchased by {quotePurchaser} on {orderPlacedAt}.
        </Box>
    )
    const imQuoteStatusMessageComponent = (
        <Box
            data-test-target={DATA_TEST_TARGET}
            display="inlineBlock"
            fontSize="14px"
            style={{
                fontWeight: '300',
                marginTop: '2px',
            }}
        >
            Quote requested! Your account manager will contact you to confirm a
            few details and to provide a quote. You can still make edits in the
            meantime.
        </Box>
    )

    const getIMQuoteStatusMessage = () => {
        if (status === REQUESTED) {
            return imQuoteStatusMessageComponent
        }
        if (isExpired) {
            return QUOTE_EXPIRED
        }
        if (
            !partsHaveProcessingDrawings &&
            areAllPartsConfigured &&
            isQuoteInRfqState
        ) {
            return IM_MANUAL_QUOTE_REQUIRED_MESSAGE
        }
        return PROJECT_REQUIREMENTS_AND_PARTS_NOT_CONFIG_MESSAGE
    }

    const getGenericQuoteStatusMessage = () => {
        if (status === REQUESTED) {
            return genericQuoteStatusMessageComponent
        }
        if (
            !partsHaveProcessingDrawings &&
            areAllPartsConfigured &&
            isQuoteInRfqState
        ) {
            return MANUAL_QUOTE_REQUIRED_MESSAGE
        }
        if (!areAllPartsConfigured || partsHaveProcessingDrawings) {
            return NO_PARTS_CONFIGURED_MESSAGE
        }
        if (status === PURCHASED) {
            return quotePurchasedStatusMessage
        }
        return null
    }

    let statusMessage = null

    if (isQuotePurchasable || quoteModelLength < 1) {
        statusMessage = ''
    }

    if (statusMessage === null) {
        statusMessage = isImQuoteType
            ? getIMQuoteStatusMessage()
            : getGenericQuoteStatusMessage()
    }

    let alertLevel = INFO_TYPE

    if (statusMessage === null) {
        statusMessage = NOT_PURCHASABLE_MESSAGE
        alertLevel = WARN_TYPE
    }

    if (statusMessage === QUOTE_EXPIRED) {
        alertLevel = WARN_TYPE
    }

    return cpqQuote
        ? {}
        : {
              statusMessage,
              alertLevel,
          }
}

export function hasFailureDfmCheck(quoteModels) {
    return _.some(quoteModels, quoteModel => {
        const dfmFeedback = _.get(
            quoteModel,
            'selectedQuoteModelRevision.dfmFeedback',
        )

        return _.some(
            dfmFeedback,
            feedback => !feedback.accepted && feedback.severity === DFM_FAILURE,
        )
    })
}

export function checkQuantityRangeAvailability(quoteModels) {
    return _.some(
        quoteModels,
        quoteModel =>
            quoteModel?.selectedQuoteModelRevision?.quantityRangeStatus ===
            QUANTITY_RANGE_UNAVAILABLE,
    )
}

export function getDateDisplay(date, dateFormat = DEFAULT_DATE_FORMAT) {
    if (date) {
        const dateUTC = moment.utc(date)

        const dateLocal = moment(dateUTC).tz(PST_TIMEZONE)

        return dateLocal.format(dateFormat)
    }

    return DEFAULT_VALUE_DASH
}

export function getShortDateDisplay(date) {
    if (date) {
        return moment(date).format(DATE_FORMAT_MM_DD_YYYY)
    }

    return NOT_AVAILABLE
}

export const useGetAttachmentTableItems = quote => {
    const hasOrphanDrawing = useMemo(
        () =>
            Boolean(
                getAttachmentTableItems(
                    quote?.quoteAttachments?.items || quote?.quoteAttachments,
                    quote?.quoteModels?.items || quote?.quoteModels,
                ).length,
            ),
        [quote],
    )

    return hasOrphanDrawing
}
